import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
    cancelGiftBatchAPI,
    cancelGiftItemAPI,
    confirmGiftBatchPaymentAPI,
    createAutomationRuleAPI,
    createGiftChargeAPI,
    deleteAutomationRuleAPI,
    deleteHeldItemAPI,
    resendGiftItemAPI,
    resendHeldItemAPI,
    sendGiftAPI,
    setSegmentRecommendedProductAPI,
    updateAutomationLimitAPI,
    updateAutomationRuleAPI,
    updateGiftSettingsAPI,
    updateTrainerBudgetPermissionAPI,
} from '../api/gifticon';
import {
    ConfirmGiftPaymentRequest,
    CreateChargeRequest,
    CreateRuleRequest,
    GiftSettings,
    SendGiftRequest,
    UpdateBudgetPermissionRequest,
    UpdateLimitRequest,
    UpdateRuleRequest,
} from '../types/gifticon';
import { GIFTICON_KEYS } from './useGifticonQueryService';

// 발송·충전 후 영향을 받는 조회 데이터를 키 기준으로 무효화한다.
// history는 필터 조합별 키가 많아 historyRoot(prefix)로 일괄 무효화.
// candidates는 세그먼트 조합별 키가 많아 candidates prefix로 일괄 무효화.
const CANDIDATES_PREFIX = [...GIFTICON_KEYS.all, 'candidates'] as const;

export const useCreateGiftChargeMutation = (accessToken: string) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (request: CreateChargeRequest) => createGiftChargeAPI(accessToken, request),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: GIFTICON_KEYS.pointAccount() });
            queryClient.invalidateQueries({ queryKey: [...GIFTICON_KEYS.all, 'charges'] });
        },
    });
};

export const useSendGiftMutation = (accessToken: string) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (request: SendGiftRequest) => sendGiftAPI(accessToken, request),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: GIFTICON_KEYS.pointAccount() });
            queryClient.invalidateQueries({ queryKey: GIFTICON_KEYS.historyRoot() });
            queryClient.invalidateQueries({ queryKey: GIFTICON_KEYS.summary() });
            queryClient.invalidateQueries({ queryKey: GIFTICON_KEYS.topProducts() });
            queryClient.invalidateQueries({ queryKey: GIFTICON_KEYS.segments() });
            queryClient.invalidateQueries({ queryKey: CANDIDATES_PREFIX });
        },
    });
};

/** 카드 결제(DIRECT_BILLING/DIRECT_CHECKOUT) 확정 — sendGift가 PENDING_PAYMENT를 반환했을 때 결제 승인 후 호출 */
export const useConfirmGiftBatchPaymentMutation = (accessToken: string) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ batchId, request }: { batchId: string; request: ConfirmGiftPaymentRequest }) =>
            confirmGiftBatchPaymentAPI(accessToken, batchId, request),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: GIFTICON_KEYS.historyRoot() });
            queryClient.invalidateQueries({ queryKey: GIFTICON_KEYS.summary() });
            queryClient.invalidateQueries({ queryKey: GIFTICON_KEYS.topProducts() });
            queryClient.invalidateQueries({ queryKey: GIFTICON_KEYS.segments() });
            queryClient.invalidateQueries({ queryKey: CANDIDATES_PREFIX });
        },
    });
};

export const useCancelGiftBatchMutation = (accessToken: string) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (batchId: string) => cancelGiftBatchAPI(accessToken, batchId),
        onSuccess: (_data, batchId) => {
            queryClient.invalidateQueries({ queryKey: GIFTICON_KEYS.historyRoot() });
            queryClient.invalidateQueries({ queryKey: GIFTICON_KEYS.historyDetail(batchId) });
            queryClient.invalidateQueries({ queryKey: GIFTICON_KEYS.pointAccount() });
            queryClient.invalidateQueries({ queryKey: GIFTICON_KEYS.segments() });
            queryClient.invalidateQueries({ queryKey: CANDIDATES_PREFIX });
        },
    });
};

export const useResendGiftItemMutation = (accessToken: string) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ batchId, itemId }: { batchId: string; itemId: string }) =>
            resendGiftItemAPI(accessToken, batchId, itemId),
        onSuccess: (_data, { batchId }) => {
            queryClient.invalidateQueries({ queryKey: GIFTICON_KEYS.historyRoot() });
            queryClient.invalidateQueries({ queryKey: GIFTICON_KEYS.historyDetail(batchId) });
        },
    });
};

export const useCancelGiftItemMutation = (accessToken: string) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ batchId, itemId }: { batchId: string; itemId: string }) =>
            cancelGiftItemAPI(accessToken, batchId, itemId),
        onSuccess: (_data, { batchId }) => {
            queryClient.invalidateQueries({ queryKey: GIFTICON_KEYS.historyRoot() });
            queryClient.invalidateQueries({ queryKey: GIFTICON_KEYS.historyDetail(batchId) });
            queryClient.invalidateQueries({ queryKey: GIFTICON_KEYS.pointAccount() });
        },
    });
};

export const useSetRecommendedProductMutation = (accessToken: string) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ segmentKey, productId }: { segmentKey: string; productId: string }) =>
            setSegmentRecommendedProductAPI(accessToken, segmentKey, productId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: GIFTICON_KEYS.segments() });
        },
    });
};

export const useUpdateGiftSettingsMutation = (accessToken: string) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (settings: GiftSettings) => updateGiftSettingsAPI(accessToken, settings),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: GIFTICON_KEYS.settings() });
            queryClient.invalidateQueries({ queryKey: GIFTICON_KEYS.segments() });
        },
    });
};

export const useCreateAutomationRuleMutation = (accessToken: string) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (request: CreateRuleRequest) => createAutomationRuleAPI(accessToken, request),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: GIFTICON_KEYS.automationRules() });
            queryClient.invalidateQueries({ queryKey: GIFTICON_KEYS.summary() });
        },
    });
};

export const useUpdateAutomationRuleMutation = (accessToken: string) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ ruleId, request }: { ruleId: string; request: UpdateRuleRequest }) =>
            updateAutomationRuleAPI(accessToken, ruleId, request),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: GIFTICON_KEYS.automationRules() });
        },
    });
};

export const useDeleteAutomationRuleMutation = (accessToken: string) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (ruleId: string) => deleteAutomationRuleAPI(accessToken, ruleId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: GIFTICON_KEYS.automationRules() });
            queryClient.invalidateQueries({ queryKey: GIFTICON_KEYS.summary() });
        },
    });
};

export const useUpdateAutomationLimitMutation = (accessToken: string) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (request: UpdateLimitRequest) => updateAutomationLimitAPI(accessToken, request),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: GIFTICON_KEYS.automationLimit() });
        },
    });
};

export const useResendHeldItemMutation = (accessToken: string) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (heldId: string) => resendHeldItemAPI(accessToken, heldId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: GIFTICON_KEYS.automationHeld() });
            queryClient.invalidateQueries({ queryKey: GIFTICON_KEYS.historyRoot() });
            queryClient.invalidateQueries({ queryKey: GIFTICON_KEYS.pointAccount() });
            queryClient.invalidateQueries({ queryKey: GIFTICON_KEYS.segments() });
            queryClient.invalidateQueries({ queryKey: CANDIDATES_PREFIX });
        },
    });
};

export const useDeleteHeldItemMutation = (accessToken: string) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (heldId: string) => deleteHeldItemAPI(accessToken, heldId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: GIFTICON_KEYS.automationHeld() });
        },
    });
};

export const useUpdateBudgetPermissionMutation = (accessToken: string) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ trainerId, request }: { trainerId: string; request: UpdateBudgetPermissionRequest }) =>
            updateTrainerBudgetPermissionAPI(accessToken, trainerId, request),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: GIFTICON_KEYS.budgetPermissions() });
        },
    });
};
